import axios, { AxiosResponse } from 'axios';
import qs from 'qs';
import urljoin from 'url-join';

import { DuplicateShiftBookingError, GetShiftsError } from 'utils/FlexApi/errors';
import { ProxyUrls } from 'constants/index';
import {
    SerializedNewShiftBooking,
    SerializedShiftBooking,
    deserializeNewShiftBooking,
    deserializeShiftBooking,
    serializeDuplicateShiftBooking,
} from 'utils/FlexApi//serialization';

export interface PayRate {
    amount: number;
    currency: string;
}

export interface DuplicateShiftBooking {
    shiftBookingId: string;
    startTime: Date;
    endTime: Date;
    supervisorName: string;
    notes: string;
}

export interface NewShiftBooking {
    shiftBookingId: string;
    workerName?: string;
}

export interface ShiftBooking {
    shiftBookingId: string; // Flex ShiftBooking.id
    workerName: string;
    workerEmail?: string;
    roleName: string;
    client: string;
    venueName: string;
    payRate: PayRate;
    jobId: string;
    breakDuration: number;
    timezone: string;
    shiftStart: Date; // Should be serialized to ISO 8601
    shiftEnd: Date; // Should be serialized to ISO 8601
}

interface Extras {
    requestId: string;
}

const isStatusCodeAllowed = (statusCode: number): boolean => {
    return statusCode <= 399 && statusCode !== 207;
};

const getErrorMessageFromResponse = (response: AxiosResponse): string => {
    // Currently this is the only error we can receive from the API after having gone through all validation.
    if (response.data.error === 'worker_not_available') {
        return 'Shift cannot be created because one already exists for this date and time. Please double check the shift details and try again.';
    }

    return response.data.error_description;
};

const REQUEST_ID_HEADER = 'X-Request-Id';

/*
    Lightweight abstraction of the Flex Import API
*/
export default class FlexApi {
    protected url: string;
    protected countryCode: string;

    constructor(appUrl: string, countryCode: string) {
        this.url = `${appUrl}/api/${countryCode}`;
        this.countryCode = countryCode;
    }

    public async getShiftBookings(from: Date, to: Date, { requestId }: Extras): Promise<ShiftBooking[]> {
        let page = 1;
        let totalPages = 1;
        const shiftBookings: ShiftBooking[] = [];
        while (page <= totalPages) {
            try {
                // Disabling Naming convention due to API contract.
                /* eslint-disable @typescript-eslint/naming-convention */
                const queryParams = {
                    from: from.toISOString(),
                    to: to.toISOString(),
                    page,
                    per_page: 30, // Assuming worst case: 2 weeks, 2 shifts per day ~28
                };
                /* eslint-enable @typescript-eslint/naming-convention */
                const url = urljoin(this.url, ProxyUrls.GET_SHIFT_BOOKINGS, `?${qs.stringify(queryParams)}`);
                const response = await axios.get(url, {
                    headers: { [REQUEST_ID_HEADER]: requestId },
                    validateStatus: isStatusCodeAllowed,
                });
                // Merge the second array into the first one
                const shiftBookingsMap = response.data as SerializedShiftBooking[];
                Array.prototype.push.apply(shiftBookings, shiftBookingsMap.map(deserializeShiftBooking));
                totalPages = Number(response.headers['x-total-pages']);
                page = page + 1;
            } catch (error) {
                if (!axios.isAxiosError(error)) {
                    throw new Error(`Unknown error in the API call: ${error}`);
                }

                const data = error.response?.data;
                if (typeof data === 'object') {
                    throw new GetShiftsError(JSON.stringify(data));
                }
                throw new GetShiftsError(data);
            }
        }

        return shiftBookings;
    }

    public async duplicateShiftBooking(
        unsavedShift: DuplicateShiftBooking,
        { requestId }: Extras
    ): Promise<NewShiftBooking> {
        const url = urljoin(this.url, `${ProxyUrls.DUPLICATE_SHIFT_BOOKING}`);
        const payload = serializeDuplicateShiftBooking(unsavedShift);
        try {
            const response = await axios.post(url, payload, {
                headers: { [REQUEST_ID_HEADER]: requestId },
                validateStatus: isStatusCodeAllowed,
            });

            return deserializeNewShiftBooking(response.data as SerializedNewShiftBooking);
        } catch (error) {
            if (!axios.isAxiosError(error)) {
                throw new Error(`Unknown error in the API call: ${error}`);
            }

            throw new DuplicateShiftBookingError(getErrorMessageFromResponse(error.response!));
        }
    }
}
